
/**
 * @typedef {'youtube'|'vimeo'|'maps'|null} PrivacyOverlayType
 */

/**
 * @typedef {Object} RemovePrivacyOverlayEventDetail
 * @property {PrivacyOverlayType[]} [types]
 */

export class PrivacyOverlay {
    /**
     * @type {Element|null}
     */
    target = null;

    /**
     * @type {Element|null}
     */
    targetParent = null;

    /**
     * @type {HTMLButtonElement|null}
     */
    button = null;

    /**
     * @type {'youtube'|'vimeo'|'maps'|null}
     */
    type = null;


    /**
     * @param {Element} target
     */
    constructor(target) {
        this.target = target;
        this.targetParent = target.parentElement;
        this.button = this.target.querySelector('button');
        this.type = this.target.querySelector('[data-type]')?.dataset.type ?? null;

        if (this.type === null || this.type.trim() === '') {
            console.error('PrivacyOverlay: No type defined', {element: this.target});
            return;
        }

        this.handlePrivacyOverlayAccepted = this.handlePrivacyOverlayAccepted.bind(this);

        if (this.isTypeAccepted()) {
            this.removeOverlay();
            return;
        }

        this.targetParent.classList.add('js-has-privacy-overlay');
        this.setupEventHandler();
    }

    setupEventHandler() {
        document.body.addEventListener('privacyoverlay:acceptedServiceUpdated', this.handlePrivacyOverlayAccepted);

        this.target.addEventListener(
            'privacyoverlay:removeForType',
            /** @param {CustomEvent<RemovePrivacyOverlayEventDetail>} event */
            (event) => {
            if (this.type && event.detail?.types?.includes(this.type)) {
                this.addTypeToSessionStorage(this.type);
            }
        });

        this.button.addEventListener('click', () => {
            this.buttonEventHandler();
        });
    }

    buttonEventHandler() {
        this.addTypeToSessionStorage();
    }

    handlePrivacyOverlayAccepted() {
        if (this.isTypeAccepted()) {
            this.removeOverlay();
        }
    }

    addTypeToSessionStorage() {
        if (typeof (Storage) === 'undefined') return;
        if (this.type === null) return;

        /**
         * @type {string[]}
         */
        const acceptedServices = sessionStorage.getItem('privacyOverlayAccepted')?.split(',') ?? [];

        if (!acceptedServices.includes(this.type)) {
            acceptedServices.push(this.type);
            sessionStorage.setItem('privacyOverlayAccepted', acceptedServices.join(','));

            // notification event for changes
            const customEvent = new CustomEvent(
                'privacyoverlay:acceptedServiceUpdated',
                {
                    bubbles: true,
                    cancelable: true,
                }
            );
            this.target.dispatchEvent(customEvent);
        }
    }

    isTypeAccepted() {
        if (typeof (Storage) === 'undefined') return false;

        /**
         * @type {string[]}
         */
        const acceptedServices = sessionStorage.getItem('privacyOverlayAccepted')?.split(',') ?? [];

        return acceptedServices.includes(this.type);
    }

    removeOverlay() {
        const customEvent = new CustomEvent(
            'privacyoverlay:close',
            {
                bubbles: true,
                cancelable: true,
            }
        );

        this.targetParent.classList.remove('js-has-privacy-overlay');
        document.body.removeEventListener('privacyoverlay:acceptedServiceUpdated', this.handlePrivacyOverlayAccepted);
        this.target.dispatchEvent(customEvent);
        this.target.remove();

        this.target = null;
        this.button = null;
    }
}
